/**
 * smokeColumnsPipeline — six colorful smoke columns rising from a fire
 * source at the bottom, advected by curl-noise turbulence.
 *
 * Each column has:
 *  - a fixed horizontal position and width
 *  - its own base color (red/orange/yellow/magenta/cyan/violet)
 *  - its own density and rise-speed modulated by audio
 *
 * Smoke is integrated via the curl of an FBM field (Perlin-style), so
 * the swirls are divergence-free and look organic. Embers rise from
 * the fire source behind the smoke. The background is a deep orange-
 * to-dark-blue gradient that brightens near the base fire.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface SmokeColumnsPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}
vec2 hash22(vec2 p) {
  p = vec2(dot(p, vec2(127.1, 311.7)),
           dot(p, vec2(269.5, 183.3)));
  return fract(sin(p) * 43758.5453);
}

float noise2(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}
float fbm(vec2 p) {
  float v = 0.0, a = 0.5;
  for (int i = 0; i < 5; i++) {
    v += a * noise2(p);
    p *= 2.05; a *= 0.5;
  }
  return v;
}

// Curl of the FBM potential (rotational flow).
vec2 curl(vec2 p) {
  float e = 0.01;
  float n_yp = fbm(p + vec2(0.0, e));
  float n_yn = fbm(p - vec2(0.0, e));
  float n_xp = fbm(p + vec2(e, 0.0));
  float n_xn = fbm(p - vec2(e, 0.0));
  return vec2(n_yp - n_yn, -(n_xp - n_xn)) / (2.0 * e);
}

#define N_COLS 6

void main() {
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  p.x *= uResolution.x / uResolution.y;

  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid  = (uMid.x  + uMid.y  + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid  = pow(clamp(mid  * 2.5, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 2.5, 0.0, 1.0), 0.7);

  float t = uTime;

  // Background: deep twilight gradient with rising fire-glow from below.
  vec3 skyTop = vec3(0.020, 0.030, 0.060);
  vec3 skyLow = vec3(0.150, 0.040, 0.020);
  vec3 col = mix(skyLow, skyTop, smoothstep(-0.5, 0.8, p.y));
  // Fire glow at base.
  float baseGlow = exp(-(p.y + 0.55) * 5.0) * exp(-dot(vec2(p.x, 0.0), vec2(p.x, 0.0)) * 1.5);
  col += vec3(1.00, 0.55, 0.20) * baseGlow * (0.5 + bass * 0.7);

  // Six smoke columns.
  for (int c = 0; c < N_COLS; c++) {
    float fc = float(c);
    float colSpacing = 1.6 / float(N_COLS - 1);
    float xCenter = -0.8 + fc * colSpacing;
    float width = 0.18 + 0.05 * hash21(vec2(fc, 11.0));

    vec3 baseColA = vec3(0.95, 0.30, 0.10); // red
    vec3 baseColB = vec3(1.00, 0.65, 0.10); // orange
    vec3 baseColC = vec3(0.95, 0.85, 0.15); // yellow
    vec3 baseColD = vec3(0.85, 0.20, 0.60); // magenta
    vec3 baseColE = vec3(0.10, 0.85, 0.95); // cyan
    vec3 baseColF = vec3(0.55, 0.25, 1.00); // violet
    vec3 colColor[6];
    colColor[0] = baseColA;
    colColor[1] = baseColB;
    colColor[2] = baseColC;
    colColor[3] = baseColD;
    colColor[4] = baseColE;
    colColor[5] = baseColF;

    vec3 smokeCol = colColor[c];

    // March upward from the base over N steps, advecting horizontally
    // using curl, accumulating density at each sample.
    float density = 0.0;
    float lift = 0.55 + 0.05 * bass; // base position of bottom
    float rise = 0.045 + 0.025 * mid + 0.020 * bass * hash21(vec2(fc, 17.0));

    vec2 q = p;
    q.y -= t * (rise + 0.05 * hash21(vec2(fc, 19.0))); // scroll upward
    q.x -= xCenter;

    const int STEPS = 14;
    for (int s = 0; s < STEPS; s++) {
      float fs = float(s);
      // Curl-noise advection, accumulating lateral offset.
      vec2 cv = curl(vec2(q.x * 1.8 + fc * 5.0, fs * 0.18 - t * 0.3));
      q.x += cv.x * 0.08;
      q.y += cv.y * 0.04;

      // Lateral Gaussian profile centered at q.x=0; width grows with s.
      float w = width + 0.025 * fs;
      float profile = exp(-(q.x * q.x) / (w * w * 0.5));
      // Vertical fade: smoke at the top is less dense.
      float vFade = smoothstep(1.0, -0.0, q.y + 0.4 + lift);
      // Higher mid → smoke billows more.
      float billow = 0.45 + 0.55 * mid
                   + 0.30 * sin(t * 0.8 + fc * 3.0 + fs * 0.4);
      density += profile * vFade * billow * 0.18;

      // Inner circulation — vortex-shaped highlights.
      float ring = exp(-pow(length(vec2(q.x * 1.2, q.y * 1.5)) - (0.2 + fs * 0.05), 2.0) * 30.0);
      density += ring * vFade * 0.10;
    }
    density = min(density, 1.4);

    // Color inside the smoke: blend base color with brighter inner shades.
    vec3 innerCol = mix(smokeCol,
                        vec3(1.00, 1.00, 0.85),
                        smoothstep(0.5, 1.2, density) * 0.4);
    col += innerCol * density;
  }

  // Embers/sparks rising from the base.
  for (int i = 0; i < 24; i++) {
    float fi = float(i);
    float seedX = hash21(vec2(fi, 31.0));
    float xPos = (seedX - 0.5) * 1.8;
    float yPos = -0.55 + 1.10 * fract(seedX * 1.4 + t * (0.15 + 0.07 * hash21(vec2(fi, 33.0)))
                                   + hash21(vec2(fi, 35.0)) * 5.0);
    // Curl shift.
    float shift = curl(vec2(xPos * 2.0, yPos * 1.5 + t * 0.5)).x;
    xPos += shift * 0.2;
    vec2 embPos = vec2(xPos, yPos);
    float ed = length(p - embPos);
    float tw = pow(0.5 + 0.5 * sin(t * (5.0 + fi * 0.4) + fi * 7.0), 6.0);
    col += vec3(1.0, 0.75, 0.30) * tw * exp(-ed * 80.0) * 0.8
         + vec3(1.0, 0.55, 0.15) * tw * exp(-ed * 30.0) * 0.2;
  }

  // Audio overall tint.
  col += vec3(0.15, 0.05, 0.02) * bass * 0.25
       + vec3(0.08, 0.10, 0.05) * mid  * 0.20
       + vec3(0.10, 0.10, 0.04) * high * 0.15;

  // Vignette.
  col *= 1.0 - dot(p, p) * 0.30;

  // Tone map + gamma.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));
  outColor = vec4(col, 1.0);
}
`;

export function computeSmokeColumnsBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.4 * Math.sin(t * 0.7);
  out[1] = 0.6 + 0.4 * Math.abs(Math.sin(t * 1.4));
  out[2] = 0.4 + 0.3 * Math.sin(t * 2.2);
  out[3] = 0.5 + 0.4 * Math.sin(t * 1.0 + 1.0);
  out[4] = 0.5 + 0.4 * Math.sin(t * 1.6 + 2.3);
  out[5] = 0.4 + 0.4 * Math.sin(t * 2.3 + 3.1);
  out[6] = 0.3 + 0.5 * Math.sin(t * 3.7 + 0.5);
  out[7] = 0.4 + 0.4 * Math.sin(t * 5.4 + 1.7);
  out[8] = 0.3 + 0.5 * Math.sin(t * 7.4 + 2.1);
}

export function computeSmokeColumnsRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const positions = [0.005, 0.020, 0.050, 0.150, 0.300, 0.500, 0.650, 0.800, 0.950];
  for (let i = 0; i < 9; i++) {
    let sum = 0, count = 0;
    for (let k = -2; k <= 2; k++) {
      const b = Math.floor(positions[i] * N) + k;
      if (b >= 0 && b < N) { sum += freqData[b]; count++; }
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createSmokeColumnsPipeline(gl: WebGL2RenderingContext): SmokeColumnsPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;

  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;

  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);

  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawSmokeColumns(
  gl: WebGL2RenderingContext,
  pipe: SmokeColumnsPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  gl.uniform2f(gl.getUniformLocation(pipe.program, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(pipe.program, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(pipe.program, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(pipe.program, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(pipe.program, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
